/**
 * Terms.tsx
 *
 * File-level: Terms of Service page that presents Edicoo's Terms of Service.
 */

import React from 'react'
import { Link } from 'react-router'
import { Button } from '../components/ui/button'

/**
 * Terms component
 *
 * Renders the Terms of Service content with clear sections and a simple layout.
 *
 * @returns {JSX.Element}
 */
export default function Terms(): JSX.Element {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm p-8">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Terms of Service</h1>
          <p className="text-sm text-gray-500">Last Updated: 25.11.2025</p>
        </header>

        <article className="prose prose-neutral max-w-none text-gray-700">
          <p>
            These Terms of Service govern your use of Edicoo’s website and all media planning, buying, digital marketing,
            and related services we provide.
          </p>

          <h2>1. Use of Our Website</h2>
          <p>
            By using our website, you agree not to misuse the platform, copy our content without permission, or attempt
            unauthorized access.
          </p>

          <h2>2. Services</h2>
          <p>
            Edicoo provides media planning, media buying, digital advertising, social media marketing, influencer
            marketing, and consulting services. Each project is governed by a separate proposal or contract agreed upon
            with the client.
          </p>

          <h2>3. Client Responsibilities</h2>
          <p>
            Clients must provide accurate information, timely approvals, and all required assets to ensure smooth
            delivery of services.
          </p>

          <h2>4. Payments</h2>
          <p>All fees must be paid according to the agreed terms. Media budgets and influencer payments are due in advance unless otherwise stated.</p>

          <h2>5. Intellectual Property</h2>
          <p>
            All creative materials those only produced by Edicoo remain our property until they are fully paid for.
            Edicoo may showcase completed work in its portfolio unless confidentiality is requested.
          </p>

          <h2>6. Third-Party Platforms</h2>
          <p>
            We work with platforms such as Google, Meta, TikTok, broadcasters, and media vendors. Edicoo is not
            responsible for outages, platform issues, or policy changes.
          </p>

          <h2>7. Limitation of Liability</h2>
          <p>
            Edicoo is not liable for indirect losses, platform errors, delays caused by third parties, or decisions made
            based on our reports.
          </p>

          <h2>8. Termination</h2>
          <p>Either party may terminate services with written notice. Completed work and booked media must be paid for in full.</p>

          <h2>9. Governing Law</h2>
          <p>These terms are governed by the laws of Iraq and the Kurdistan Regional Government (KRG).</p>

          <p>
            For questions, contact <a href="mailto:info@edicoo.com" className="text-orange-600 hover:underline">info@edicoo.com</a>
          </p>
        </article>

        <div className="mt-8 flex items-center justify-between">
          <Button asChild variant="outline" className="bg-transparent">
            <Link to="/">Back to Home</Link>
          </Button>

          <Button asChild size="sm" className="bg-orange-600 text-white hover:bg-orange-500">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}